import {AceLoggerService} from '@logging/ace-logger.service';
import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, OutputRefSubscription, ViewChild} from '@angular/core';
import {ImageCroppedEvent, ImageCropperComponent, LoadedImage} from 'ngx-image-cropper';
import {ImageProcessingService} from '../../services';
import {AceLogger} from '@logging/ace-logger';
import {I18nMessage} from '@app/shared/api/model/i18n';


export interface Dimensions {
  width?: number | null;
  height?: number | null;
}

@Component({
  selector: 'app-ace-image-cropper',
  templateUrl: './ace-image-cropper.component.html',
  styleUrls: ['./ace-image-cropper.component.scss'],
  standalone: false
})
export class AceImageCropperComponent implements OnInit, OnChanges, OnDestroy {

  @Output() croppedImageBase64 = new EventEmitter<string>();

  @Input()
  maxFileSize = 14194304;
  @Input()
  maintainAspectRatio = true;
  @Input()
  aspectRatio: number;
  @Input()
  resizeToWidth: number;
  @Input()
  format = 'png';
  @Input()
  uploadedFile: {fileName: string, url: string, data: string};
  @Input() cropperMinDimensions: Dimensions | null = null;
  @Input() previewDisplayDimensions: Dimensions| null = null;
  @ViewChild(ImageCropperComponent, {static: true}) imageCropper: ImageCropperComponent | null = null;

  private readonly logger: AceLogger;
  private cropperImageLoadedSubscription: OutputRefSubscription | null = null;
  private lastLoadedImage: LoadedImage | null = null;

  imageFileBase64: string;
  croppedImg: string;
  cropperReady = false;
  photoWarningMessages: I18nMessage[] | null = null;

  constructor(private imageProcessingService: ImageProcessingService, logService: AceLoggerService) {
    this.logger = logService.create(AceImageCropperComponent.name);
  }

  ngOnInit(): void {
    if (this.imageCropper) {
      this.cropperImageLoadedSubscription = this.imageCropper.imageLoaded.subscribe(loadedImage => {
        this.lastLoadedImage = loadedImage;
        this.photoWarningMessages = this.getMinSizeWarnings(loadedImage, this.cropperMinDimensions);
      });
    } else {
      this.logger.error('Expected imageCropper component reference but got: ', this.imageCropper);
    }
  }

  ngOnChanges(changes) {
    if (changes.uploadedFile) {
      const value: {fileName: string, url: string, data: string} = changes.uploadedFile.currentValue;
      if (value) {
        let file: File;
        try {
          file = new File([this.imageProcessingService.dataURItoBlob(value.data)], value.fileName);
        } catch (e) {
          // Must be in IE or Edge that do not support File constructor
          const f = this.imageProcessingService.dataURItoBlob(value.data);
          const lmd = new Date();
          f['lastModifiedDate'] = lmd;
          f['lastModified'] = lmd.getTime();
          f['name'] = value.fileName;
          file = <File>f;
        }
        this.imageFileBase64 = value.data;
      } else {
        this.imageFileBase64 = null;
      }
    }

    if (changes.cropperMinDimensions) {
      this.photoWarningMessages = this.getMinSizeWarnings(this.lastLoadedImage, this.cropperMinDimensions);
    }
  }

  ngOnDestroy() {
    this.cropperImageLoadedSubscription?.unsubscribe()
  }

  imageCroppedBase64(event: ImageCroppedEvent) {
    this.croppedImg = event.base64;
    this.croppedImageBase64.emit(event.base64);
  }

  imageLoaded() {
    this.cropperReady = true;
  }

  imageLoadFailed() {
    this.logger.error('Load failed');
  }

  fileInputChangeListener(files: File[]) {
    this.logger.debug('fileInputChangeListener', files);
    this.photoWarningMessages = null;
    this.lastLoadedImage = null;
    if (files?.length) {
      this.imageProcessingService.fileToDataURI(files[0])
        .subscribe({
          next: (fileBase64) => {
            this.imageFileBase64 = fileBase64;
          },
          error: (error) => {
            throw error;
          }
        });
    } else {
      this.cropperReady = false;
      this.imageFileBase64 = null;
      this.croppedImageBase64.emit(null);
    }
  }

  private getMinSizeWarnings(loadedImage: LoadedImage | null, minimumDimensions: Dimensions | null): I18nMessage[] | null {
    if (loadedImage && minimumDimensions?.width && minimumDimensions?.height) {
      const photoWarningMessages: I18nMessage[] = [];
      if (loadedImage.original.size.width < minimumDimensions.width) {
        photoWarningMessages.push({
          messageKey: 'Photo width is smaller than minimum width',
          parameters: {width: loadedImage.original.size.width, minWidth: minimumDimensions.width}
        });
      }
      if (loadedImage.original.size.height < minimumDimensions.height) {
        photoWarningMessages.push({
          messageKey: 'Photo height is smaller than minimum height',
          parameters: {height: loadedImage.original.size.height, minHeight: minimumDimensions.height}
        });
      }
      if (photoWarningMessages.length) {
        return photoWarningMessages;
      }
    }
    return null;
  }
}
